import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  ArrowLeft, User, Trophy, BookOpen, Target, BarChart,
  Menu, Camera, Save, X, Mail, Github, LogOut, Edit2
} from 'lucide-react';
import { Card } from './ui/card';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Avatar, AvatarFallback, AvatarImage } from './ui/avatar';
import {
  getHighScore,
  getStatistics,
  setStatistics,
  setHighScore,
  getCollectedVerses,
  getCompletedBooksCount,
  getTotalChaptersCompleted
} from '../utils/localStorage';
import { toast } from 'sonner';
import { SideMenu } from './SideMenu';
import { supabase } from '../lib/supabase';
import { getCurrentUser, updateUserProfile, logoutUser } from '../utils/auth';

export const ProfilePage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const fileInputRef = useRef(null);

  // Game stats state (to be reactive after sync)
  const [gameStats, setGameStats] = useState(() => getStatistics());
  const [profileHighScore, setProfileHighScore] = useState(() => getHighScore());
  const [profileCompletedBooks, setProfileCompletedBooks] = useState(() => getCompletedBooksCount());
  const [profileChaptersCompleted, setProfileChaptersCompleted] = useState(() => getTotalChaptersCompleted());

  // Calculate average score reactively
  const averageScore = gameStats.gamesPlayed > 0
    ? Math.round(gameStats.totalScore / gameStats.gamesPlayed)
    : 0;

  // User state
  const [user, setUser] = useState(null);
  const [profile, setProfile] = useState(null);
  const [isEditing, setIsEditing] = useState(location.state?.editMode || false);
  const [isLoading, setIsLoading] = useState(true);
  const [isSaving, setIsSaving] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);

  const handlePlayClick = () => {
    navigate('/select-book');
  };

  // Form states
  const [formData, setFormData] = useState({
    fullName: '',
    email: '',
    avatarUrl: ''
  });

  // Fetch user and profile on mount
  useEffect(() => {
    const fetchUserData = async () => {
      setIsLoading(true);
      try {
        const currentUser = await getCurrentUser();
        if (!currentUser) {
          navigate('/login');
          return;
        }
        setUser(currentUser);

        // Fetch extended profile data
        const { data: profileData, error } = await supabase
          .from('profiles')
          .select('*')
          .eq('id', currentUser.id)
          .single();

        if (error && error.code !== 'PGRST116') {
          console.error('Error fetching profile:', error);
        }

        setProfile(profileData);
        setFormData({
          fullName: profileData?.full_name || currentUser.user_metadata?.full_name || '',
          email: currentUser.email || '',
          avatarUrl: profileData?.avatar_url || currentUser.user_metadata?.avatar_url || ''
        });

        // Sync statistics from Supabase to LocalStorage
        if (profileData) {
          const remoteStats = {
            totalScore: profileData.total_score || 0,
            totalVersesRevealed: profileData.total_verses_revealed || 0,
            gamesPlayed: profileData.games_played || 0,
            booksCompleted: profileData.books_completed || []
          };

          // Merge remote stats with local
          const localStats = getStatistics();
          const mergedStats = {
            ...localStats,
            totalScore: Math.max(localStats.totalScore, remoteStats.totalScore),
            totalVersesRevealed: Math.max(localStats.totalVersesRevealed, remoteStats.totalVersesRevealed),
            gamesPlayed: Math.max(localStats.gamesPlayed, remoteStats.gamesPlayed),
            booksCompleted: Array.isArray(remoteStats.booksCompleted)
              ? [...new Set([...(localStats.booksCompleted || []), ...remoteStats.booksCompleted])]
              : (localStats.booksCompleted || [])
          };

          setStatistics(mergedStats);
          setGameStats(mergedStats);

          if (profileData.high_score) {
            setHighScore(profileData.high_score, false);
            setProfileHighScore(Math.max(getHighScore(), profileData.high_score));
          }

          // Update other reactive stats
          setProfileCompletedBooks(mergedStats.booksCompleted.length);
          setProfileChaptersCompleted(getTotalChaptersCompleted());

          // If remote had more data, notify user (subtly)
          if (remoteStats.totalScore > localStats.totalScore) {
            toast.info('Progreso sincronizado desde la nube');
          }
        }
      } catch (err) {
        console.error('Error in fetchUserData:', err);
      } finally {
        setIsLoading(false);
      }
    };

    fetchUserData();
  }, [navigate]);

  const handleFileChange = async (e) => {
    const file = e.target.files[0];
    if (!file) return;

    // Validate type and size
    if (!file.type.startsWith('image/')) {
      toast.error('Solo se permiten imágenes');
      return;
    }
    if (file.size > 2 * 1024 * 1024) {
      toast.error('La imagen es demasiado grande (máx 2MB)');
      return;
    }

    try {
      setIsSaving(true);
      const fileExt = file.name.split('.').pop();
      const fileName = `${user.id}-${Math.random()}.${fileExt}`;
      const filePath = `${fileName}`;

      // Upload to supabase storage
      const { error: uploadError } = await supabase.storage
        .from('avatars')
        .upload(filePath, file);

      if (uploadError) throw uploadError;

      // Get public URL
      const { data: { publicUrl } } = supabase.storage
        .from('avatars')
        .getPublicUrl(filePath);

      setFormData(prev => ({ ...prev, avatarUrl: publicUrl }));
      toast.success('Foto subida correctamente');
    } catch (error) {
      console.error('Error uploading avatar:', error);
      toast.error('Error al subir la foto');
    } finally {
      setIsSaving(false);
    }
  };

  const handleSaveProfile = async (e) => {
    e.preventDefault();
    setIsSaving(true);

    try {
      // 1. Update Auth Metadata
      const authResult = await updateUserProfile({
        full_name: formData.fullName,
        avatar_url: formData.avatarUrl
      });

      if (!authResult.success) throw new Error(authResult.error);

      // 2. Update profiles table
      const { error: profileError } = await supabase
        .from('profiles')
        .upsert({
          id: user.id,
          full_name: formData.fullName,
          avatar_url: formData.avatarUrl,
          updated_at: new Date().toISOString()
        });

      if (profileError) throw profileError;

      // 3. Update Email if changed (wait, email update in Supabase usually requires confirmation)
      if (formData.email !== user.email) {
        const { error: emailError } = await supabase.auth.updateUser({
          email: formData.email
        });
        if (emailError) throw emailError;
        toast.info('Se ha enviado un correo de confirmación a tu nueva dirección');
      }

      setProfile(prev => ({ ...prev, full_name: formData.fullName, avatar_url: formData.avatarUrl }));
      setIsEditing(false);
      toast.success('Perfil actualizado correctamente');
    } catch (error) {
      console.error('Error saving profile:', error);
      toast.error(error.message || 'Error al guardar el perfil');
    } finally {
      setIsSaving(false);
    }
  };

  const achievements = [
    {
      id: 'first-book',
      title: 'Primer Libro',
      description: 'Completa tu primer libro bíblico',
      unlocked: profileCompletedBooks >= 1,
      icon: '📗',
      color: 'text-green-500',
      progress: Math.min(profileCompletedBooks, 1),
      total: 1,
    },
    {
      id: 'dedicated-player',
      title: 'Jugador Dedicado',
      description: 'Juega 50 partidas',
      unlocked: gameStats.gamesPlayed >= 50,
      icon: '🎮',
      color: 'text-blue-500',
      progress: Math.min(gameStats.gamesPlayed, 50),
      total: 50,
    },
    {
      id: 'collector',
      title: 'Coleccionista',
      description: 'Colecciona 1,000 versículos',
      unlocked: gameStats.totalVersesRevealed >= 1000,
      icon: '📚',
      color: 'text-purple-500',
      progress: Math.min(gameStats.totalVersesRevealed, 1000),
      total: 1000,
    },
    {
      id: 'disciplined',
      title: 'Disciplinado',
      description: 'Revela 8 libros',
      unlocked: profileCompletedBooks >= 8,
      icon: '✍️',
      color: 'text-amber-500',
      progress: Math.min(profileCompletedBooks, 8),
      total: 8,
    },
    {
      id: 'bible-student',
      title: 'Estudiante Bíblico',
      description: 'Revela 16 libros',
      unlocked: profileCompletedBooks >= 16,
      icon: '👨‍🎓',
      color: 'text-sky-500',
      progress: Math.min(profileCompletedBooks, 16),
      total: 16,
    },
    {
      id: 'high-scorer',
      title: 'Puntuador Alto',
      description: 'Alcanza 10,000 puntos',
      unlocked: gameStats.totalScore >= 10000,
      icon: '🏆',
      color: 'text-yellow-500',
      progress: Math.min(gameStats.totalScore, 10000),
      total: 10000,
    },
    {
      id: 'bible-master',
      title: 'Maestro Bíblico',
      description: 'Completa 33 libros',
      unlocked: profileCompletedBooks >= 33,
      icon: '🌟',
      color: 'text-orange-500',
      progress: Math.min(profileCompletedBooks, 33),
      total: 33,
    },
    {
      id: 'bible-scholar',
      title: 'Erudito Bíblico',
      description: 'Completa los 66 libros',
      unlocked: profileCompletedBooks >= 66,
      icon: '👑',
      color: 'text-indigo-500',
      progress: Math.min(profileCompletedBooks, 66),
      total: 66,
    },
  ];

  const unlockedCount = achievements.filter(a => a.unlocked).length;

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-background">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-background to-secondary/20 pb-24">
      <SideMenu open={menuOpen} onOpenChange={setMenuOpen} />

      {/* Minimal Header */}
      <header className="max-w-2xl mx-auto px-4 py-6">
        <div className="flex items-center justify-between">
          <button
            onClick={() => setMenuOpen(true)}
            className="p-2 hover:bg-secondary rounded-full interactive"
          >
            <Menu className="w-6 h-6" />
          </button>
          <h1 className="text-xl font-bold">Perfil</h1>
          <div className="w-10"></div>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-2">
        {!isEditing && (
          /* View Mode Profile Card */
          <Card className="p-8 mb-6 text-center relative shadow-lg border-2 border-primary/5 bg-white">
            <button
              onClick={() => setIsEditing(true)}
              className="absolute right-4 top-4 p-2 text-primary hover:bg-primary/10 rounded-full transition-colors"
              title="Editar Perfil"
            >
              <Edit2 className="w-5 h-5" />
            </button>

            <div className="mb-4 flex justify-center">
              <Avatar className="w-28 h-28 border-4 border-background shadow-2xl">
                {formData.avatarUrl ? (
                  <AvatarImage src={formData.avatarUrl} alt={formData.fullName} className="object-cover" />
                ) : null}
                <AvatarFallback className="bg-gradient-to-br from-primary to-accent text-primary-foreground text-3xl font-bold">
                  {formData.fullName?.charAt(0) || user?.email?.charAt(0) || 'J'}
                </AvatarFallback>
              </Avatar>
            </div>

            <h2 className="text-2xl font-bold mb-1" style={{ fontFamily: 'Space Grotesk, sans-serif' }}>
              {formData.fullName || 'Jugador'}
            </h2>
            <p className="text-sm text-muted-foreground mb-4">{user?.email}</p>
            <p className="text-xs text-muted-foreground mb-6 italic">
              Miembro desde {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'hoy'}
            </p>

            <div className="flex justify-center gap-3">
              <Badge className="bg-primary hover:bg-primary/90 text-primary-foreground px-6 py-1 text-sm shadow-md">
                Nivel {profileCompletedBooks}
              </Badge>
              <Badge variant="secondary" className="px-6 py-1 text-sm">
                {gameStats.totalVersesRevealed} Versículos
              </Badge>
            </div>
          </Card>
        )}

        {isEditing && (
          <div className="mb-6 text-center">
            <h2 className="text-xl font-bold">Editar Perfil</h2>
            <p className="text-sm text-muted-foreground">Actualiza tu información personal</p>
          </div>
        )}
        {isEditing ? (
          /* Edit Profile Form */
          <Card className="p-6 shadow-lg border-2 border-primary/10">
            <form onSubmit={handleSaveProfile} className="space-y-6">
              {/* Photo Upload */}
              <div className="flex flex-col items-center gap-4 mb-6">
                <div className="relative group">
                  <Avatar className="w-32 h-32 border-4 border-muted cursor-pointer hover:opacity-90 transition-opacity">
                    {formData.avatarUrl ? (
                      <AvatarImage src={formData.avatarUrl} className="object-cover" />
                    ) : null}
                    <AvatarFallback className="text-4xl bg-muted text-muted-foreground">
                      {formData.fullName?.charAt(0) || 'J'}
                    </AvatarFallback>
                  </Avatar>
                  <button
                    type="button"
                    onClick={() => fileInputRef.current?.click()}
                    className="absolute bottom-1 right-1 p-2 bg-primary text-primary-foreground rounded-full shadow-lg hover:scale-110 transition-transform"
                  >
                    <Camera className="w-5 h-5" />
                  </button>
                  <input
                    type="file"
                    ref={fileInputRef}
                    onChange={handleFileChange}
                    className="hidden"
                    accept="image/*"
                  />
                </div>
                <Label className="text-xs text-muted-foreground">Haz clic en la cámara para subir foto</Label>
              </div>

              <div className="space-y-4">
                <div className="space-y-2">
                  <Label htmlFor="fullName">Nombre Completo</Label>
                  <div className="relative">
                    <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="fullName"
                      value={formData.fullName}
                      onChange={(e) => setFormData(prev => ({ ...prev, fullName: e.target.value }))}
                      placeholder="Tu nombre"
                      className="pl-10 h-12"
                      required
                    />
                  </div>
                </div>

                <div className="space-y-2">
                  <Label htmlFor="email">Correo Electrónico</Label>
                  <div className="relative">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                    <Input
                      id="email"
                      type="email"
                      value={formData.email}
                      onChange={(e) => setFormData(prev => ({ ...prev, email: e.target.value }))}
                      placeholder="correo@ejemplo.com"
                      className="pl-10 h-12"
                      required
                    />
                  </div>
                  <p className="text-[10px] text-muted-foreground px-1 italic">
                    Nota: Cambiar tu correo requerirá confirmación vía email.
                  </p>
                </div>
              </div>

              <div className="flex gap-3 pt-4">
                <Button
                  type="button"
                  variant="ghost"
                  className="flex-1 h-12"
                  onClick={() => setIsEditing(false)}
                  disabled={isSaving}
                >
                  <X className="w-4 h-4 mr-2" />
                  Cancelar
                </Button>
                <Button
                  type="submit"
                  className="flex-1 h-12 bg-primary"
                  disabled={isSaving}
                >
                  {isSaving ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  ) : (
                    <Save className="w-4 h-4 mr-2" />
                  )}
                  Guardar Cambios
                </Button>
              </div>
            </form>
          </Card>
        ) : (
          /* Profile Statistics and Achievements */
          <>
            <div className="grid grid-cols-2 gap-4 mb-6">
              <Card className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-primary/10 rounded-full flex items-center justify-center">
                  <Trophy className="w-5 h-5 text-primary" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-tight">Mejor Puntuación</p>
                  <p className="text-lg font-bold">{profileHighScore.toLocaleString()}</p>
                </div>
              </Card>
              <Card className="p-4 flex items-center gap-3">
                <div className="w-10 h-10 bg-accent/10 rounded-full flex items-center justify-center">
                  <Target className="w-5 h-5 text-accent" />
                </div>
                <div>
                  <p className="text-[10px] text-muted-foreground uppercase font-bold tracking-tight">Partidas Jugadas</p>
                  <p className="text-lg font-bold">{gameStats.gamesPlayed}</p>
                </div>
              </Card>
            </div>

            <Card className="p-6 mb-6">
              <h3 className="text-xs font-bold mb-4 text-muted-foreground uppercase tracking-widest flex items-center gap-2">
                <BarChart className="w-4 h-4" />
                Estadísticas Detalladas
              </h3>
              <div className="grid grid-cols-1 gap-1">
                <div className="flex justify-between items-center py-3 border-b border-border/50 text-sm">
                  <span className="text-muted-foreground">Versículos Revelados</span>
                  <span className="font-bold text-primary">{gameStats.totalVersesRevealed}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border/50 text-sm">
                  <span className="text-muted-foreground">Capítulos Revelados</span>
                  <span className="font-bold">{profileChaptersCompleted}</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border/50 text-sm">
                  <span className="text-muted-foreground">Libros Completados</span>
                  <span className="font-bold text-accent">{profileCompletedBooks} / 66</span>
                </div>
                <div className="flex justify-between items-center py-3 border-b border-border/50 text-sm">
                  <span className="text-muted-foreground">Promedio por Partida</span>
                  <span className="font-bold">{averageScore.toLocaleString()}</span>
                </div>
                <div className="flex justify-between items-center py-3 text-sm">
                  <span className="text-muted-foreground">Puntuación Total</span>
                  <span className="font-bold">{gameStats.totalScore.toLocaleString()}</span>
                </div>
              </div>
            </Card>

            {/* Achievements */}
            <h3 className="text-xs font-bold mb-4 text-muted-foreground uppercase tracking-[0.2em] px-1 flex items-center gap-2">
              <Trophy className="w-4 h-4 text-primary" />
              Logros
            </h3>
            <div className="grid grid-cols-2 gap-3 mb-8">
              {achievements.map((achievement) => {
                const isUnlocked = achievement.unlocked;
                const progressPercent = Math.min((achievement.progress / achievement.total) * 100, 100);

                return (
                  <Card
                    key={achievement.id}
                    className={`p-3 flex flex-col gap-2 transition-all hover:shadow-lg border-2 shadow-sm bg-card ${isUnlocked ? 'border-primary/20 bg-primary/[0.02]' : 'border-border/50 opacity-90'
                      }`}
                  >
                    {/* Icon with yellow border circle matching top stats */}
                    <div className="flex items-center gap-2">
                      <div className={`flex-shrink-0 w-10 h-10 rounded-xl border-2 ${isUnlocked ? 'border-yellow-400 bg-yellow-50 shadow-[0_0_10px_rgba(250,204,21,0.15)]' : 'border-gray-200 bg-gray-50'
                        } flex items-center justify-center text-xl shadow-sm`}>
                        {achievement.icon}
                      </div>
                      {isUnlocked && (
                        <div className="bg-yellow-400 text-yellow-900 rounded-full p-0.5 shadow-sm ml-auto">
                          <Trophy className="w-2 h-2" />
                        </div>
                      )}
                    </div>

                    <div className="flex-1 min-w-0">
                      <h4 className={`font-bold text-xs leading-tight mb-1 ${isUnlocked ? 'text-foreground' : 'text-gray-500'}`}>
                        {achievement.title}
                      </h4>

                      <p className="text-[10px] text-muted-foreground leading-snug mb-2 line-clamp-2">
                        {achievement.description}
                      </p>

                      {/* Progress Section */}
                      <div className="space-y-1">
                        <div className="flex justify-between items-end text-[8px] font-black uppercase tracking-wider">
                          <span className={isUnlocked ? 'text-primary' : 'text-muted-foreground'}>
                            {achievement.progress.toLocaleString()} / {achievement.total.toLocaleString()}
                          </span>
                          {isUnlocked && <span className="text-yellow-600 font-bold">¡OK!</span>}
                        </div>
                        <div className="w-full h-1 bg-secondary/50 rounded-full overflow-hidden">
                          <div
                            className={`h-full transition-all duration-1000 ease-out rounded-full ${isUnlocked ? 'bg-gradient-to-r from-yellow-400 to-yellow-600' : 'bg-muted'
                              }`}
                            style={{ width: `${progressPercent}%` }}
                          ></div>
                        </div>
                      </div>
                    </div>
                  </Card>
                );
              })}
            </div>


            <Button
              onClick={handlePlayClick}
              size="lg"
              className="w-full h-16 text-lg font-bold bg-primary hover:bg-primary/90 text-primary-foreground rounded-full shadow-lg pulse-glow mt-8"
            >
              <span className="mr-3 text-xl">▶</span>
              JUGAR
            </Button>

            <Button
              variant="outline"
              className="w-full h-12 text-destructive border-destructive/20 hover:bg-destructive/5 hover:text-destructive mt-4"
              onClick={async () => {
                await logoutUser();
                navigate('/login');
              }}
            >
              <LogOut className="w-4 h-4 mr-2" />
              Cerrar Sesión
            </Button>
          </>
        )}
      </main>

      {/* Bottom Navigation */}
      <nav className="fixed bottom-0 left-0 right-0 bg-card/80 backdrop-blur-md border-t border-border shadow-2xl z-50">
        <div className="max-w-2xl mx-auto px-6 py-3 flex justify-between items-center">
          <button onClick={() => navigate('/')} className="flex flex-col items-center gap-1 group">
            <div className="w-6 h-6 rounded-md bg-muted-foreground/20 group-hover:bg-primary/20 transition-colors flex items-center justify-center">
              <div className="w-2 h-2 bg-muted-foreground group-hover:bg-primary rounded-sm"></div>
            </div>
            <span className="text-[10px] text-muted-foreground group-hover:text-primary transition-colors">Inicio</span>
          </button>
          <button onClick={() => navigate('/collection')} className="flex flex-col items-center gap-1 group">
            <BookOpen className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-[10px] text-muted-foreground group-hover:text-primary transition-colors">Colección</span>
          </button>
          <button onClick={() => navigate('/ranking')} className="flex flex-col items-center gap-1 group">
            <BarChart className="w-5 h-5 text-muted-foreground group-hover:text-primary transition-colors" />
            <span className="text-[10px] text-muted-foreground group-hover:text-primary transition-colors">Ranking</span>
          </button>
          <button onClick={() => navigate('/profile')} className="flex flex-col items-center gap-1 text-primary">
            <User className="w-5 h-5" />
            <span className="text-[10px] font-bold">Perfil</span>
          </button>
        </div>
      </nav>
    </div>
  );
};